import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Customer } from '../customer';
import { Permissions } from '../models/enums/permissions';
import { StorefrontBasic } from 'src/app/admin/service/queries/storefront-basic.fragment';

export interface User {
  userId: number;
  fullName: string;
  customer?: Customer;
  storefrontId?: string;
  email: string;
  returnUrl: string;
  permissions: Permissions[];
}

const storageKeys = {
  authToken: 'auth',
  storefrontId: 'storefrontId',
  typeFilter: 'typeFilter',
  categoryFilter: 'categoryFilter',
  statusFilter: 'statusFilter'
};

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private storefrontSub$ = new BehaviorSubject<StorefrontBasic>(null);
  private userSub$ = new BehaviorSubject<User>(null);

  get storefront$(): Observable<StorefrontBasic> {
    return this.storefrontSub$.asObservable();
  }

  get customerId(): number | null {
    const customer = this.userSub$.value && this.userSub$.value.customer;
    return customer ? customer.customerId : null;
  }

  get storefrontId(): string | null {
    return sessionStorage.getItem(storageKeys.storefrontId);
  }

  get currencyCode(): string | null {
    const sf = this.storefrontSub$.value;
    return sf ? sf.currencyCode : null;
  }

  get user(): User | null {
    return this.userSub$.value;
  }

  get user$(): Observable<User> {
    return this.userSub$.asObservable();
  }

  get typeFilter(): string[] {
    const typeFilter = sessionStorage.getItem(storageKeys.typeFilter);
    return typeFilter ? JSON.parse(typeFilter) : [];
  }

  get categoryFilter(): string[] {
    const categoryFilter = sessionStorage.getItem(storageKeys.categoryFilter);
    return categoryFilter ? JSON.parse(categoryFilter) : [];
  }

  get statusFilter(): string[] {
    const statusFilter = sessionStorage.getItem(storageKeys.statusFilter);
    return statusFilter ? JSON.parse(statusFilter) : [];
  }

  start(user: User, storefront?: StorefrontBasic): void {
    this.userSub$.next(user);
    if (storefront) {
      this.setStorefront(storefront);
    }
  }

  end(): void {
    sessionStorage.clear();
  }

  setStorefront(storefront: StorefrontBasic): void {
    sessionStorage.setItem(storageKeys.storefrontId, storefront.storefrontId);
    this.storefrontSub$.next(storefront);
  }

  clearStorefront(): void {
    sessionStorage.removeItem(storageKeys.storefrontId);
    this.storefrontSub$.next(null);
  }

  setTypeFilter(type: string[]): void {
    sessionStorage.setItem(storageKeys.typeFilter, JSON.stringify(type));
  }

  setCategoryFilter(category: string[]): void {
    sessionStorage.setItem(storageKeys.categoryFilter, JSON.stringify(category));
  }

  setStatusFilter(status: string[]): void {
    sessionStorage.setItem(storageKeys.statusFilter, JSON.stringify(status));
  }

  // Keep the token in sessionStorage, not localStorage. The token decides which customer this tab is
  // acting as, and the app opens one customer per tab. sessionStorage is per-tab, so each tab keeps
  // its own customer. localStorage and cookies are shared across all tabs, so a new login in one tab
  // would switch every other open tab to the wrong customer.
  storeToken(jwt: string): void {
    sessionStorage.setItem(storageKeys.authToken, jwt);
  }

  getToken(): string | null {
    return sessionStorage.getItem(storageKeys.authToken);
  }
}
